import { createReducer, on } from '@ngrx/store';
import * as RideMonitoringActions from './ride-monitoring.actions';
import { CustomerServiceNoteDetail, OperationsAlertType, RideMonitoringAlert, RideMonitoringUtil } from '@uzurv/clara';
import { cloneDeep } from 'lodash';
import { ReservationListItemVm } from '../../components/reservation';
import { SafeUrl } from '@angular/platform-browser';
import { TransitProgram } from '@uzurv/dots';
import { isNullOrUndefined } from '@uzurv/core';

export interface RideMonitoringState {
  alerts: Array<RideMonitoringAlert>;
  operationsAlertTypes: Array<OperationsAlertType>;
  transitPrograms: Array<TransitProgram>;
  selectedRideStatusNames?: Array<string>;
  selectedTransitProgramIds?: Array<number>;
  selectedOperationsAlertTypeIds?: Array<number>;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  selectedAlert: RideMonitoringAlert;
  shouldPollAlerts: boolean;
  selectedReservationListItemVm: ReservationListItemVm;
  driverProfilePictureUrl: SafeUrl;
  notes: Array<CustomerServiceNoteDetail>;
}

export const initialState: RideMonitoringState = {
  alerts: [],
  operationsAlertTypes: [],
  transitPrograms: [],
  selectedRideStatusNames: [],
  selectedTransitProgramIds: [],
  selectedOperationsAlertTypeIds: [],
  pageNumber: 0,
  pageSize: 20,
  totalCount: 0,
  selectedAlert: null,
  shouldPollAlerts: false,
  selectedReservationListItemVm: null,
  driverProfilePictureUrl: null,
  notes: []
};

export const rideMonitoring = createReducer(
  initialState,
  on(RideMonitoringActions.RestoreState, (_, action) => {
    return {
      ...action.state
    };
  }),

  on(RideMonitoringActions.FetchOperationsAlertTypesSuccess, (state, action) => {
    const sortedOperationsAlertTypes = RideMonitoringUtil.sortOperationsAlertTypes(action.operationsAlertTypes);
    return { ...state, operationsAlertTypes: sortedOperationsAlertTypes };
  }),

  on(RideMonitoringActions.FetchTransitProgramsSuccess, (state, action) => {
    const sortedTransitPrograms = RideMonitoringUtil.sortTransitPrograms(action.transitPrograms);
    return { ...state, transitPrograms: sortedTransitPrograms };
  }),

  on(RideMonitoringActions.SelectRideStatuses, (state, action) => {
    return { ...state, selectedRideStatusNames: action.rideStatusNames };
  }),

  on(RideMonitoringActions.SelectTransitProgramIds, (state, action) => {
    return {
      ...state,
      selectedTransitProgramIds: action.transitProgramIds
    };
  }),

  on(RideMonitoringActions.SelectOperationsAlertTypeIds, (state, action) => {
    return { ...state, selectedOperationsAlertTypeIds: action.operationAlertTypeIds };
  }),

  on(RideMonitoringActions.initializeFilters, (state, action) => {
    return {
      ...state,
      selectedRideStatusNames: action.rideStatusNames,
      selectedTransitProgramIds: action.transitProgramIds,
      selectedOperationsAlertTypeIds: action.selectedOperationsAlertTypeIds
    };
  }),

  on(RideMonitoringActions.ClearFilters, state => {
    return {
      ...state,
      selectedAlert: null,
      selectedRideStatusNames: [],
      selectedTransitProgramIds: [],
      selectedOperationsAlertTypeIds: []
    };
  }),

  on(RideMonitoringActions.RestoreState, (_, action) => action.state),

  on(RideMonitoringActions.RefreshAlertsSuccess, (state, action) => {
    let currentAlerts: Array<RideMonitoringAlert> = cloneDeep(state.alerts);
    let resultAlerts: Array<RideMonitoringAlert> = cloneDeep(action.alerts.results);

    resultAlerts.forEach(alert => {
      currentAlerts = RideMonitoringUtil.addOrReplaceAlertInList(alert, currentAlerts);
    });

    // Remove any alerts that don't exist in the action results.
    currentAlerts = currentAlerts.filter(currentAlert =>
      resultAlerts.some(resultAlert => resultAlert.operationAlertId === currentAlert.operationAlertId)
    );

    let updatedSelectedAlert = state?.selectedAlert;

    if (!isNullOrUndefined(state.selectedAlert)) {
      updatedSelectedAlert = currentAlerts.find(alert => alert.operationAlertId === state.selectedAlert.operationAlertId);
    }

    return {
      ...state,
      alerts: RideMonitoringUtil.sortRideMonitoringAlerts(currentAlerts),
      pageNumber: RideMonitoringUtil.calculatePageNumber(currentAlerts, initialState.pageSize),
      totalCount: action.alerts.totalCount,
      selectedAlert: updatedSelectedAlert
    };
  }),

  on(RideMonitoringActions.FetchNextAlertsPageSuccess, (state, action) => {
    let currentAlerts: Array<RideMonitoringAlert> = cloneDeep(state.alerts);
    let resultAlerts: Array<RideMonitoringAlert> = cloneDeep(action.alerts.results);

    resultAlerts.forEach(alert => {
      currentAlerts = RideMonitoringUtil.addOrReplaceAlertInList(alert, currentAlerts);
    });

    return {
      ...state,
      alerts: RideMonitoringUtil.sortRideMonitoringAlerts(currentAlerts),
      pageNumber: RideMonitoringUtil.calculatePageNumber(currentAlerts, initialState.pageSize)
    };
  }),

  on(RideMonitoringActions.StartPollingAlerts, (state, action) => {
    return {
      ...state,
      shouldPollAlerts: true
    };
  }),

  on(RideMonitoringActions.StopPollingAlerts, (state, action) => {
    return {
      ...state,
      shouldPollAlerts: false
    };
  }),

  on(RideMonitoringActions.SetSelectedAlertSuccess, (state, action) => {
    return {
      ...state,
      selectedAlert: action.alert,
      selectedReservationListItemVm: action.reservationListItemVm
    };
  }),

  on(RideMonitoringActions.GetDriverProfilePictureSuccess, (state, action) => {
    return {
      ...state,
      driverProfilePictureUrl: action.safeUrl
    };
  }),

  on(RideMonitoringActions.RemoveSelectedAlert, (state, action) => {
    return {
      ...state,
      selectedAlert: null,
      selectedReservationListItemVm: null
    };
  }),

  on(RideMonitoringActions.GetNotesSuccess, (state, action) => {
    return {
      ...state,
      notes: action.notes
    };
  }),

  on(RideMonitoringActions.ResolveRideMonitoringAlertSuccess, (state, action) => {
    let alerts: Array<RideMonitoringAlert> = cloneDeep(state.alerts);
    alerts = alerts.filter(alert => alert.operationAlertId !== state.selectedAlert.operationAlertId);

    return {
      ...state,
      alerts: alerts,
      totalCount: state.totalCount - 1, // Decrement by one so that the infinite scroll does not fire.
      selectedAlert: null,
      selectedReservationListItemVm: null,
      pageNumber: RideMonitoringUtil.calculatePageNumber(alerts, initialState.pageSize)
    };
  }),

  on(RideMonitoringActions.DispatchToLyftSuccess, (state, action) => {
    return {
      ...state,
      selectedAlert: null,
      selectedReservationListItemVm: null
    };
  })
);
